/* LawyersDirectory — صفحة دليل محامي العراق العامّة.
   - للمحامي المُسجَّل دُخول: تَعرض بطاقته في الأَعلى مَع تَنبيه إكمال إن لَزم.
   - للزّوّار / العُملاء: تَعرض القائمة فَقط (بِدون بِطاقة شَخصيّة).
   - تَدعَم البحث + فَلاتر المُحافَظة / القَضاء / الاختصاص. */
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { MapPin, Crown, Users, CaretRight, X, Star, Buildings } from "@phosphor-icons/react";
import IraqFlag from "../components/IraqFlag";
import DirectoryFilters from "../components/lawyers/DirectoryFilters";
import VerifiedBadge, { GradeChip, HotChip } from "../components/lawyers/VerifiedBadge";
import { publicLawyers, avatarUrl } from "../lib/api";
import { gradeLabel } from "../lib/lawyerVerification";
import { useUser } from "../context/UserAuthContext";

// Tier-aware card styling. Elite gets a luxe gold frame + Elite chip,
// Firm gets a navy frame + Firm chip, Pro keeps the default chrome with
// a small "مُحتَرِف" pill. Free lawyers render as the existing baseline.
const TIER_STYLES = {
  elite: {
    container:
      "bg-gradient-to-br from-[#FFFBEC] via-white to-[#FFF6DD] border-[#D4AF37] " +
      "ring-2 ring-[#D4AF37]/40 shadow-[0_10px_30px_rgba(212,175,55,0.25)] " +
      "hover:shadow-[0_18px_42px_rgba(212,175,55,0.35)] hover:border-[#B8860B]",
    avatarRing: "ring-2 ring-[#D4AF37]",
    chip: {
      icon: Star,
      label: "النُّخبَة",
      classes: "bg-[#D4AF37] text-[#0A192F]",
    },
  },
  firm: {
    container:
      "bg-gradient-to-br from-[#F1F5FB] via-white to-[#E6EEF8] border-[#1F3556] " +
      "ring-2 ring-[#1F3556]/30 shadow-[0_10px_30px_rgba(31,53,86,0.22)] " +
      "hover:shadow-[0_18px_42px_rgba(31,53,86,0.30)] hover:border-[#0A192F]",
    avatarRing: "ring-2 ring-[#1F3556]",
    chip: {
      icon: Buildings,
      label: "مَكتَب",
      classes: "bg-[#0A192F] text-[#D4AF37]",
    },
  },
  pro: {
    container:
      "bg-white border-[#EAEAEA] hover:border-[#D4AF37] " +
      "shadow-[0_2px_18px_rgba(10,25,47,0.05)] hover:shadow-[0_8px_28px_rgba(10,25,47,0.10)]",
    avatarRing: "ring-2 ring-[#D4AF37]/30",
    chip: {
      icon: null,
      label: "مُحتَرِف",
      classes: "bg-[#0A192F]/8 text-[#0A192F]",
    },
  },
  free: {
    container:
      "bg-white border-[#EAEAEA] hover:border-[#D4AF37] " +
      "shadow-[0_2px_18px_rgba(10,25,47,0.05)] hover:shadow-[0_8px_28px_rgba(10,25,47,0.10)]",
    avatarRing: "ring-2 ring-[#D4AF37]/30",
    chip: null,
  },
};

function TierChip({ tier }) {
  const cfg = TIER_STYLES[tier]?.chip;
  if (!cfg) return null;
  const Icon = cfg.icon;
  return (
    <span
      data-testid={`tier-chip-${tier}`}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-bold tracking-[0.1em] ${cfg.classes}`}
    >
      {Icon && <Icon size={11} weight="fill" />}
      {cfg.label}
    </span>
  );
}

function Card({ card }) {
  const initials = (card.name || "؟").split(" ").slice(0, 2).map((s) => s[0] || "").join("");
  const grade = gradeLabel(card.grade);
  const tier = card.tier_id || "free";
  const style = TIER_STYLES[tier] || TIER_STYLES.free;
  return (
    <Link
      to={`/lawyers/${card.slug}`}
      data-testid={`directory-card-${card.slug}`}
      data-tier={tier}
      className={`block border rounded-[20px] p-4 transition-all ${style.container}`}
    >
      <div className="flex items-center gap-3.5">
        <div className={`w-14 h-14 rounded-full overflow-hidden bg-[#F5F5F5] flex items-center justify-center shrink-0 ${style.avatarRing}`}>
          {card.avatar_id ? (
            <img src={avatarUrl(card.avatar_id)} alt="" className="w-full h-full object-cover" loading="lazy" />
          ) : (
            <span className="text-[16px] font-bold text-[#5A6470]">{initials}</span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-[15px] font-bold text-[#0A192F] truncate">{card.name}</span>
            <TierChip tier={tier} />
            {card.verified && <VerifiedBadge size={13} />}
            {card.is_hot && <HotChip size="xs" />}
            {card.rank <= 3 && <Crown size={13} weight="fill" className="text-[#D4AF37]" />}
          </div>
          {grade && (
            <div className="mt-1">
              <GradeChip grade={grade.short} full={grade.full} color={grade.color} size="xs" />
            </div>
          )}
          {card.specialty && (
            <div className="text-[12.5px] text-[#5A6470] mt-1 line-clamp-1">{card.specialty}</div>
          )}
          {(card.city || card.governorate || card.district) && (
            <div className="flex items-center gap-1 text-[11px] text-[#5A6470] mt-1">
              <MapPin size={11} weight="fill" />
              <span>{[card.governorate || card.city, card.district].filter(Boolean).join(" · ")}</span>
              {card.years_of_experience > 0 && (
                <span className="opacity-80"> · {card.years_of_experience} سَنة خِبرة</span>
              )}
            </div>
          )}
        </div>
        <CaretRight size={16} weight="bold" className="text-[#D4AF37] shrink-0" />
      </div>
      {card.bio && (
        <p className="mt-3 text-[12.5px] text-[#0A192F] leading-relaxed line-clamp-2">{card.bio}</p>
      )}
    </Link>
  );
}

export default function LawyersDirectory() {
  const { user } = useUser();
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();
  const [q, setQ] = useState(sp.get("q") || "");
  const [governorate, setGovernorate] = useState(sp.get("governorate") || "");
  const [district, setDistrict] = useState(sp.get("district") || "");
  const [specialty, setSpecialty] = useState(sp.get("specialty") || "");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);

  // مُحاذاة لون شَريط الحالة (iOS notch) + خَلفيّة الـ body مع لون الصَّفحة العُلويّ
  useEffect(() => {
    const prevBg = document.body.style.backgroundColor;
    document.body.style.backgroundColor = "#FFFFFF";
    const meta = document.querySelector('meta[name="theme-color"]');
    const prevTheme = meta ? meta.getAttribute("content") : null;
    if (meta) meta.setAttribute("content", "#FFFFFF");
    return () => {
      document.body.style.backgroundColor = prevBg;
      if (meta && prevTheme !== null) meta.setAttribute("content", prevTheme);
    };
  }, []);

  const closePage = () => nav("/");

  // مَرجع شَريط العنوان لِقياس ارتفاعه ديناميكيّاً (مَع safe-area)
  const headerRef = useRef(null);
  const [headerH, setHeaderH] = useState(72);

  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setHeaderH(el.getBoundingClientRect().height));
    ro.observe(el);
    setHeaderH(el.getBoundingClientRect().height);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    let alive = true;
    const params = {};
    if (q) params.q = q;
    if (governorate) params.governorate = governorate;
    if (district) params.district = district;
    if (specialty) params.specialty = specialty;
    publicLawyers(params)
      .then((d) => { if (alive) { setItems(d?.lawyers || []); setLoading(false); } })
      .catch(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick]);

  const submit = (e) => {
    e?.preventDefault();
    const next = {};
    if (q) next.q = q;
    if (governorate) next.governorate = governorate;
    if (district) next.district = district;
    if (specialty) next.specialty = specialty;
    setSp(next);
    setLoading(true);
    setTick((t) => t + 1);
  };

  // إخفاء بطاقة المحامي صاحب الحساب من القائمة (تَظهَر فَقط بِشكل بِطاقة شخصيّة أعلى الصفحة)
  const otherLawyers = user ? items.filter((c) => c.id !== user.id) : items;

  return (
    <div
      className="min-h-[100dvh] font-tajawal"
      dir="rtl"
      style={{
        background:
          "radial-gradient(ellipse 900px 500px at 50% -10%, rgba(212,175,55,0.18) 0%, rgba(212,175,55,0) 55%), linear-gradient(180deg,#FFFFFF 0%,#FAFAFA 35%,#F3F3F3 100%)",
      }}
    >
      {/* شَريط العنوان — يُرسَل عبر Portal إلى body لِتَفادي مَشاكل transform في <main>،
          fixed في الأَعلى لا يَتَحَرَّك مع المُحتَوى، نَفس نَمَط شَريط "قضاياي"
          (زرّ دائري + عنوان + إجراء دائري) مع تَدَرُّج أَبيَض شَفّاف + backdrop blur. */}
      {createPortal(
        <header
          ref={headerRef}
          className="lg:hidden fixed top-0 inset-x-0 z-50"
          dir="rtl"
          style={{
            paddingTop: "calc(env(safe-area-inset-top, 0px) + 12px)",
            paddingBottom: "10px",
            background:
              "linear-gradient(180deg, rgba(255,255,255,0.92) 0%, rgba(255,255,255,0.72) 60%, rgba(255,255,255,0) 100%)",
            backdropFilter: "blur(8px)",
            WebkitBackdropFilter: "blur(8px)",
          }}
          data-testid="dir-header"
        >
          <div className="px-4 flex items-center justify-between">
            {/* زرّ الإغلاق X — start (يَمين في RTL) */}
            <button
              type="button"
              onClick={closePage}
              aria-label="إغلاق"
              data-testid="dir-close-btn"
              className="w-10 h-10 rounded-full bg-white shadow-[0_2px_8px_rgba(10,25,47,0.10)] flex items-center justify-center active:scale-95 transition-transform"
            >
              <X size={18} weight="bold" className="text-[#0A192F]" />
            </button>

            {/* العنوان — في الوَسَط */}
            <div className="flex-1 text-center px-3">
              <h2 className="text-[17px] font-bold text-[#0A192F] leading-tight" data-testid="dir-title">
                مَحامو العراق
              </h2>
            </div>

            {/* العَلَم العراقي — end (يَسار في RTL) — مَرسوم كَما هو بِدون أيّ تَغليف */}
            <IraqFlag className="w-[35px] h-[22px] shrink-0" testid="dir-flag" />
          </div>
        </header>,
        document.body,
      )}

      {/* فاصِل لِتَعويض ارتفاع الشَّريط الثَّابِت (موبايل فَقط) */}
      <div aria-hidden="true" className="lg:hidden" style={{ height: `${headerH}px` }} />

      {/* وَصف قَصير تَحت الشَّريط */}
      <section className="px-4 pt-3 pb-3 max-w-3xl mx-auto text-center">
        <p className="text-[12.5px] text-[#5A6470] leading-relaxed max-w-xl mx-auto">
          مُرتَّبون حَسب الخِبرة والأَداء الفِعليّ على المنصّة — لا إعلانات مَدفوعة، لا تَصويت.
        </p>
      </section>

      <main className="px-4 pb-16 max-w-3xl mx-auto" data-testid="lawyers-directory-main">
        {/* فَلاتر البَحث */}
        <div className="mb-4">
          <DirectoryFilters
            q={q} setQ={setQ}
            governorate={governorate} setGovernorate={setGovernorate}
            district={district} setDistrict={setDistrict}
            specialty={specialty} setSpecialty={setSpecialty}
            onSubmit={submit}
          />
        </div>

        {/* العَدّاد */}
        <div className="mb-3 flex items-center gap-2 text-[12.5px] text-[#5A6470]">
          <Users size={14} weight="duotone" className="text-[#D4AF37]" />
          <span>
            {loading
              ? "جارٍ التَّحميل..."
              : `${otherLawyers.length} مَحامياً ${user ? "آخَر" : "مُتاحاً"}${governorate ? ` في ${governorate}` : ""}`}
          </span>
        </div>

        {/* قائمة المحامين الآخَرين */}
        {!loading && otherLawyers.length === 0 && (
          <div className="bg-white border border-dashed border-[#EAEAEA] rounded-[20px] p-10 text-center text-[13px] text-[#5A6470]">
            لا يوجد محامون مُطابقون لِبَحثك حالياً.
          </div>
        )}
        <ul className="space-y-2.5" data-testid="lawyers-directory-list">
          {otherLawyers.map((card) => (
            <li key={card.id}><Card card={card} /></li>
          ))}
        </ul>
      </main>
    </div>
  );
}
